import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomerLookupService } from '../../customer/customer-lookups.service';
import { IndividualDetailService } from '../../customer/detail/individual-detail.service';
import { OrganizationDetailService } from '../../customer/detail/organization-detail.service';
import { CustomerAddress } from '../../customer/models/customer-address.model';
import { CustomerIdentificationType } from '../../customer/models/enums/customer-identification-type.enum';
import { CustomerIndividualNameSuffixType } from '../../customer/models/enums/customer-individual-name-suffix-type.enum';
import { CustomerImageService } from '../../customer/shared/customer-image.service';
import { CustomerService } from '../../customer/shared/customer.service';
import { IJurisdiction, Jurisdiction } from '../../foundation/models/enums/jurisdiction.enum';
import { CustomerModel } from '../models/customer.model';
import { OwnershipCustomerRelationshipModel } from '../models/ownership-customer-relationship.model';

@Injectable()
export class TitleRegistrationCustomerService {
    private jurisdictionTypes: Jurisdiction = new Jurisdiction();
    private customerIdentificationType: CustomerIdentificationType = new CustomerIdentificationType();

    constructor(private customerService: CustomerService, private individualService: IndividualDetailService, private organizationService: OrganizationDetailService, private customerLookups: CustomerLookupService, private customerImage: CustomerImageService) {
    }

    /**
     * Gets the details of a customer record based on the customer number.
     * @param customerNumber: Individual or organization customer number
     */
    getCustomerDetails = (customerNumber: number, formatName: Function, setMailingAddress: Function, formatAddress: Function): Observable<any> => {
        let self = this;
        var customerObservable$ = self.getBasicCustomers([customerNumber])
            .pipe(
                map(result => {
                    var customers = <CustomerModel[]> result;
                    if (customers && customers.length) {
                        let customer = customers[0];
                        let ownershipCustomerRelationshipModel: OwnershipCustomerRelationshipModel = new OwnershipCustomerRelationshipModel();
                        ownershipCustomerRelationshipModel.customer = customer;
                        ownershipCustomerRelationshipModel.mailingAddress = customer.mailingAddressDisplayValue; // for legacy purposes

                        if (customer.physicalAddress) { // TODO: the following code needs to be revisited again
                            ownershipCustomerRelationshipModel.physicalAddressEmissionTypeAreaTypeID = customer.physicalAddress.emissionAreaTypeId;
                        } else if (customer.mailingAddress /* && customer.mailingAddress.isSameAsPhysical*/) {// there is not a single record in the database with the flag set
                            ownershipCustomerRelationshipModel.physicalAddressEmissionTypeAreaTypeID = customer.mailingAddress.emissionAreaTypeId;
                        }
                        return ownershipCustomerRelationshipModel;
                    }
                    return null;
                })
            );

        return customerObservable$;
    };

    /**
     * Get the customer Photo for an individual customer
     * @param customerNumber: Individual or organization customer number
     */
    getCustomerPhoto = (customerNumber: number) => this.individualService.getData(customerNumber)
        .pipe(
            map(result => result.photo)
        );

    public getBasicCustomers = (customerNumberIds: number[]): Observable<any> => this.customerService.getBasicCustomers(customerNumberIds);

    getPlacardCustomerDetails = (customerNumber: number, formatName: Function, setContacts: Function): Observable<any> => {
        let self = this;
        var customerObservable$ = self.getBasicCustomers([customerNumber])
            .pipe(
                map(result => {
                    var customers = <CustomerModel[]> result;
                    if (customers && customers.length) {
                        let customer = customers[0];
                        setContacts(result, customer);
                        return customer;
                    }
                    return null;
                })
            );

        return customerObservable$;
    };

    /**
     * Formats the individual's name into a single string from the different input strings.
     * @param firstName: Individual's first name
     * @param middleName: Individual's middle name
     * @param lastName: Individual's last name
     * @param suffixTypeId: Individual's suffix type
     */
    formatIndividualName = (firstName: string, middleName: string, lastName: string, suffixTypeId: number, customerIndividualNameSuffixTypes: any): string => {
        let fullName: string;

        if (!firstName && !middleName) {
            fullName = lastName;
        } else if (!middleName) {
            fullName = firstName + ' ' + lastName;
        } else {
            fullName = firstName + ' ' + middleName + ' ' + lastName;
        }

        if (suffixTypeId) {
            const suffixTypes: CustomerIndividualNameSuffixType = new CustomerIndividualNameSuffixType();
            // suffixTypes.FromId(suffixTypeId).displayValue;
            fullName = fullName + ' ' + suffixTypes.FromId(suffixTypeId).displayValue;
        }

        return fullName;
    };

    /**
     * This method is deprecated
     * Formats the address
     * @param address: Address for the customer
     */
    formatAddress = (address: CustomerAddress, jurisdictions: any): string => {
        let formattedAddress: string;
        const jurisdiction: IJurisdiction = this.jurisdictionTypes.GetAll().find(i => +i.id === address.jurisdictionId);
        let jurisdictionName = '';
        if (jurisdiction) {
            jurisdictionName = jurisdiction.code;
        }

        if (!address.addressLine2) {
            formattedAddress = address.addressLine1 + ', ' + address.city + ', ' + jurisdictionName + ' ' + address.postalZipCode;
        } else {
            formattedAddress = address.addressLine1 + ' ' + address.addressLine2 + ', ' + address.city + ', ' + jurisdictionName + ' ' + address.postalZipCode;
        }

        return formattedAddress;
    };

    /**
     * Formats the address
     * @param address: Address for the customer
     */
    formatAddressFromCustomerAddress = (address: CustomerAddress): string => {
        const jurisdictions = new Jurisdiction().GetAll();

        let formattedAddress: string;
        const jurisdiction: IJurisdiction = this.jurisdictionTypes.GetAll().find(i => +i.id === address.jurisdictionId);
        let jurisdictionName = '';
        if (jurisdiction) {
            jurisdictionName = jurisdiction.displayValue;
        }

        if (!address.addressLine2) {
            formattedAddress = address.addressLine1 + ', ' + address.city + ', ' + jurisdictionName + ' ' + address.postalZipCode;
        } else {
            formattedAddress = address.addressLine1 + ' ' + address.addressLine2 + ', ' + address.city + ', ' + jurisdictionName + ' ' + address.postalZipCode;
        }

        return formattedAddress;
    };
    setContacts = (source: any, targetCustomer: CustomerModel): void => {
        for (var x = 0; x < source.contacts.length; x++) {
            var contact = source.contacts[x];

            switch (contact.contactMethodTypeId) {
                case 1:
                case 2:
                case 3:
                    if (!targetCustomer.phones) {
                        targetCustomer.phones = new Array<any>();
                    }

                    targetCustomer.phones.push(source.contacts[x].customerContactValue);
                    break;
                case 4:
                    targetCustomer.email = source.contacts[x].customerContactValue;
                    break;
                default:
                    break;
            }
        }
    };

    setMailingAddress = (addresses: CustomerAddress[], targetCustomer: OwnershipCustomerRelationshipModel, formatAddress: Function, jurisdictions: any): void => {
        if (addresses != null) {
            const mailingAddress = addresses.find(c => c.addressTypeId === 3);
            if (mailingAddress) {
                targetCustomer.mailingAddress = formatAddress(mailingAddress, jurisdictions);
            }

            const residentialAddress = addresses.find(c => c.addressTypeId === 1);
            if (residentialAddress) {
                targetCustomer.mailingAddress = formatAddress(residentialAddress, jurisdictions);
            }
        }
    };
}
